import { api } from "@/lib/api";
import type {
  Notification,
  NotificationAuditEntry,
  NotificationTargetFilter,
  NotificationsListResponse,
} from "@/lib/schema";

export interface CreateNotificationPayload {
  title: string;
  body: string;
  category: "system" | "account_billing" | "content";
  scope: "all" | "segment" | "individual";
  channels: ("push" | "email")[];
  status: "draft" | "scheduled" | "send_now";
  targetFilter?: NotificationTargetFilter;
  targetUserId?: string;
  scheduledAt?: string;
  repeatFrequency?: "daily" | "weekly" | "monthly";
  repeatTimeOfDay?: string; // "HH:mm"
  repeatDaysOfWeek?: number[]; // 0..6
  repeatDaysOfMonth?: number[]; // 1..31
  repeatCount?: number;
  repeatUntil?: string; // ISO datetime
}

export interface PreviewPayload {
  scope: "all" | "segment" | "individual";
  channels: ("push" | "email")[];
  targetFilter?: NotificationTargetFilter;
  targetUserId?: string;
}

export interface ListNotificationsParams {
  status?: string;
  category?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export const notificationsService = {
  async list(
    params: ListNotificationsParams = {},
  ): Promise<NotificationsListResponse> {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== "" && v !== null) {
        search.append(k, String(v));
      }
    });
    const qs = search.toString();
    return api.get<NotificationsListResponse>(
      `/api/notifications${qs ? `?${qs}` : ""}`,
    );
  },

  async get(id: string): Promise<Notification> {
    return api.get<Notification>(`/api/notifications/${id}`);
  },

  async create(payload: CreateNotificationPayload): Promise<Notification> {
    return api.post<Notification>("/api/notifications", payload);
  },

  async preview(
    payload: PreviewPayload,
  ): Promise<{ estimatedRecipients: number }> {
    return api.post<{ estimatedRecipients: number }>(
      "/api/notifications/preview",
      payload,
    );
  },

  async send(id: string): Promise<Notification> {
    return api.post<Notification>(`/api/notifications/${id}/send`);
  },

  async cancel(id: string): Promise<Notification> {
    return api.post<Notification>(`/api/notifications/${id}/cancel`);
  },

  async audit(id: string): Promise<NotificationAuditEntry[]> {
    return api.get<NotificationAuditEntry[]>(`/api/notifications/${id}/audit`);
  },

  async softDelete(id: string): Promise<{ ok: true; deletedCount: number }> {
    return api.delete<{ ok: true; deletedCount: number }>(
      `/api/notifications/${id}`,
    );
  },
};
